// Replace Authenticator
// ----------------------
// Self-service TOTP rotation for the signed-in user. Lets someone moving to a
// new phone enrol a fresh authenticator without an admin/support reset. The
// user keeps their session throughout; on success the old device stops working.
//
// Only surfaced (by the caller) when the user already has TOTP enrolled — see
// User.tsx, which gates on cognitologin.getPreferredMfa().

import React, { useState, useCallback, useEffect } from "react";
import { KeyRound, Loader2, ShieldCheck, X } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormField } from "@/components/ui/form-field";
import { EMPTY_STRING } from "@/constants/definitions";
import CognitoLoginClass from "@/models/cognitoLogin";

const TOTP_CODE_LENGTH = 6;

interface ReplaceAuthenticatorModalProps {
  show: boolean;
  close: () => void;
  cognitologin: CognitoLoginClass;
  // Label shown in the authenticator app entry (the user's email).
  accountLabel: string;
}

export default function ReplaceAuthenticatorModal({
  show,
  close,
  cognitologin,
  accountLabel,
}: ReplaceAuthenticatorModalProps) {
  const [otpauthUri, setOtpauthUri] = useState<string | null>(null);
  const [manualSecret, setManualSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [setupFailed, setSetupFailed] = useState(false);
  const [code, setCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Generate a fresh secret each time the modal opens. Re-opening after a
  // cancel must not reuse a stale/half-applied secret.
  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setLoading(true);
    setSetupFailed(false);
    setOtpauthUri(null);
    setManualSecret(null);
    setCode("");
    setErrorMessage("");
    setDone(false);

    (async () => {
      const enrolment = await cognitologin.beginTotpReenrollment(accountLabel);
      if (cancelled) return;
      if (!enrolment) {
        setSetupFailed(true);
      } else {
        setOtpauthUri(enrolment.otpauthUri);
        setManualSecret(enrolment.secret);
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [show, cognitologin, accountLabel]);

  const handleConfirm = useCallback(async () => {
    if (code.length !== TOTP_CODE_LENGTH) {
      setErrorMessage(`Enter the ${TOTP_CODE_LENGTH}-digit code from your authenticator app`);
      return;
    }
    setSubmitting(true);
    setErrorMessage("");
    try {
      const result = await cognitologin.confirmTotpReenrollment(code);
      if (result === EMPTY_STRING) {
        setDone(true);
      } else {
        setErrorMessage(result);
        setCode("");
      }
    } catch {
      setErrorMessage("An unexpected error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [code, cognitologin]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={close} />

      <div className="relative z-10 w-full max-w-md mx-4 rounded-xl bg-white shadow-xl border border-gray-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-blue-500" />
            Replace authenticator
          </h2>
          <button
            type="button"
            onClick={close}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {done ? (
            <div className="text-center py-4">
              <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-emerald-50 border border-emerald-200">
                <ShieldCheck className="h-5 w-5 text-emerald-600" />
              </div>
              <p className="text-sm font-medium text-gray-900">Authenticator replaced</p>
              <p className="mt-1 text-xs text-gray-500">
                Your previous authenticator no longer works. Use the new one next time you sign in.
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-4">
                Scan this QR code with your new authenticator app, then enter the 6-digit code to confirm.
                Your old device stops working once you confirm.
              </p>

              {errorMessage && (
                <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                  {errorMessage}
                </div>
              )}

              <div className="mb-4">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                  </div>
                ) : setupFailed ? (
                  <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                    Couldn't start setup. Please close and try again.
                  </div>
                ) : (
                  <>
                    <div className="flex justify-center">
                      <div className="rounded-lg border border-gray-200 p-3">
                        {otpauthUri && <QRCodeSVG value={otpauthUri} size={160} />}
                      </div>
                    </div>
                    {manualSecret && (
                      <div className="mt-3 text-center">
                        <p className="text-xs text-gray-400">Can't scan? Enter this key manually:</p>
                        <p className="mt-1 font-mono text-xs text-gray-600 break-all select-all">
                          {manualSecret}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>

              <FormField label="Verification code" htmlFor="reenrollCode">
                <Input
                  id="reenrollCode"
                  type="text"
                  inputMode="numeric"
                  placeholder="123456"
                  value={code}
                  onChange={(changeEvent) => {
                    setCode(changeEvent.target.value.replace(/\D/g, "").slice(0, TOTP_CODE_LENGTH));
                    setErrorMessage("");
                  }}
                  onKeyDown={(keyEvent) => { if (keyEvent.key === "Enter") handleConfirm(); }}
                  autoComplete="one-time-code"
                  maxLength={TOTP_CODE_LENGTH}
                  disabled={submitting || loading || setupFailed}
                  leftIcon={<KeyRound className="h-4 w-4" />}
                />
              </FormField>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          {done ? (
            <Button size="sm" onClick={close} className="bg-blue-600 hover:bg-blue-500 text-white">
              Done
            </Button>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={close} disabled={submitting}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleConfirm}
                disabled={submitting || loading || setupFailed || code.length !== TOTP_CODE_LENGTH}
                className="bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50"
              >
                {submitting ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Confirming...</>
                ) : (
                  "Confirm"
                )}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
